import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import {
  getJlgFirstAndLastEmiDates,
  calculateEmiAmount,
  generateLoanSummaryForMember,
  getReportWindow,
  isValidPaidReceipt,
  isLoanCovered,
} from "../../../utils/utils";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";

import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";

const JlgMasterDataReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDate, setFromDate] = useState("2024-07-01");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(`${BASE_URL}/api/getJlgMasterReportData`, {
          params: { id },
        });

        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  useEffect(() => {
    // Process all data first
    const processedData = reportData.map((item) => {
      // Calculate EMI months paid - total paid ever
      const emiMonthsPaid = item.receiptsDetails
        ? new Set(item.receiptsDetails.filter(
          (receipt) =>
            isValidPaidReceipt(receipt)
        ).map(receipt => receipt.emiDate)).size
        : 0;

      // Get loan summary data - from start of loan
      const loanSummary = generateLoanSummaryForMember(
        item,
        "2000-01-01",
        toDate || new Date().toISOString().split("T")[0]
      );

      // Merge all data
      return {
        ...item,
        emiMonthsPaid,
        ...loanSummary,
      };
    });

    let filtered = processedData;

    if (fromDate && toDate) {
      // Shared whole-day window, so Master and Outstanding cannot drift apart.
      const { start, end } = getReportWindow(fromDate, toDate);
      filtered = filtered.filter((item) => {
        // Further filter data based on branchManagerStatusUpdatedAt within the date range
        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        return updatedAt >= start && updatedAt <= end;
      });
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item.memberName || "").toLowerCase();
        const customerId = String(item.customerId || "").toLowerCase();
        const loanId = String(item.loanId || "").toLowerCase();
        const phoneNumber = String(item.phoneNumber || "").toLowerCase();
        const aadharNo = String(item.aadharNo || "").toLowerCase();
        const applicationId = String(item.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [fromDate, toDate, reportData, searchTerm]);

  // Define columns with label and render logic
  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    {
      label: "Center Name",
      render: (data) => data.fk_member_details_belongsTo_center_centerId.name,
    },
    {
      label: "Day Order",
      render: (data) =>
        "Week No:" +
        data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder,
    },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) =>
        `${data.permanentDoorNo},${data.permanentStreetNo},${data.permanentPanchayat},${data.permanentTaluk},${data.permanentDistrict}-${data.permanentPincode}`,
    },
    { label: "Contact No", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    { label: "Loan Status", render: (data) => data.loanStatus },


    {
      label: "Sub Category",
      render: (data) => data.businessDetails?.natureOfBusiness || "",
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    { label: "Funder Name", render: (data) => data.agencyName },
    { label: "Loan Account No", render: (data) => data.loanId },
    {
      label: "Rate of Interest",
      render: (data) => data.proposedLoanDetails.rateOfInterest,
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" &&
          data.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString(
            "en-US",
            {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }
          )
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getJlgFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-US") : "";
      },
    },
    {
      label: "Disbursed Amount",
      render: (data) =>
        data.branchManagerStatus === "disbursed"
          ? data.sanctionedLoanAmountBySanctionCommittee
          : "",
    },
    {
      label: "Tenure In Months",
      render: (data) => data.proposedLoanDetails.tenureInMonths,
    },
    {
      label: "EMI months Paid",
      render: (data) => data.emiMonthsPaid,
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    { label: "Employee Code", render: (data) => data.employeeId },

    {
      label: "Outstanding Principal",
      render: (data) => data.totalOutstandingPrincipal || 0,
    },
    {
      label: "Outstanding Interest",
      render: (data) => data.totalOutstandingInterest || 0,
    },
    {
      label: "Principal Paid",
      render: (data) => data.principalPaid || 0,
    },
    {
      label: "Interest Paid",
      render: (data) => data.interestPaid || 0,
    },
    {
      label: "Overdue Principal",
      render: (data) => data.overduePrincipal || 0,
    },
    {
      label: "Overdue Interest",
      render: (data) => data.overdueInterest || 0,
    },
    {
      label: "Total PAR",
      render: (data) => data.totalPAR || 0,
    },
    {
      label: "DPD",
      render: (data) => data.dpd || 0,
    },
    {
      label: "Bucket",
      render: (data) => data.bucket || "-",
    },
    {
      label: "Last Paid Date",
      render: (data) => data.lastPaidDate || "-",
    },
    {
      label: "First Overdue Date",
      render: (data) => data.firstOverdueDate || "-",
    },
    {
      label: "Is Demised(Insurance)",
      render: (data) => "No",
    },
    {
      label: "EMI Start Date",
      render: (data) => {
        const { firstEmiDate } = getJlgFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.fk_member_details_belongsTo_center_centerId.bmMeetingDayOrder
        );
        return firstEmiDate ? firstEmiDate.toLocaleDateString("en-US") : "";
      },
    },
    { label: "Prospect ID", render: (data) => data.ApplicationId },
    {
      label: "Processing Fee",
      render: (data) => (data.processingCharge ? data.processingCharge : ""),
    },
    { label: "GST", render: (data) => (data.gst ? data.gst : "") },
    {
      label: "Insurance",
      render: (data) => (isLoanCovered(data) ? data.insuranceAmount : 0),
    },
    {
      // Money written off when a foreclosure was settled below the payable
      // amount. Blank for loans that were never foreclosed.
      label: "Waiver Amount",
      render: (data) =>
        data?.fk_member_details_hasOne_member_foreclosure_approval_memberId
          ?.waiverAmount ?? "",
    },
    {
      label: "Waiver Reason",
      render: (data) =>
        data?.fk_member_details_hasOne_member_foreclosure_approval_memberId
          ?.waiverReason || "",
    },
    {
      label: "Security Deposit",
      render: (data) => {
        const emiAmount = calculateEmiAmount(
          data.sanctionedLoanAmountBySanctionCommittee,
          data.proposedLoanDetails.rateOfInterest,
          data.proposedLoanDetails.tenureInMonths
        );
        return emiAmount ? Math.round(emiAmount) : "";
      },
    },
    {
      label: "EMI Amount",
      render: (data) => {
        const emiAmount = calculateEmiAmount(
          data.sanctionedLoanAmountBySanctionCommittee,
          data.proposedLoanDetails.rateOfInterest,
          data.proposedLoanDetails.tenureInMonths
        );
        return emiAmount ? Math.round(emiAmount) : "";
      },
    },
    {
      label: "Loan Purpose",
      render: (data) => data.businessDetails?.businessType || "",
    },
    {
      label: "Aadhar Number",
      render: (data) => data.aadharNo,
    },
    {
      label: "Pan Number",
      render: (data) => (data.panNo ? data.panNo : ""),
    },
    {
      label: "Voter Id",
      render: (data) =>
        data.anotherIdentity === "Voter Id" ? data.anotherIdentityId : "",
    },
    {
      label: "Bank Account Number",
      render: (data) => data.bankDetails.accountNumber,
    },
    {
      label: "IFSC Code",
      render: (data) => data.bankDetails.ifscCode,
    },
    {
      label: "Bank Name",
      render: (data) => data.bankDetails.bankName,
    },
    {
      label: "Bank Branch",
      render: (data) => data.bankDetails.bankBranch,
    },
    // Add more columns as needed with custom render logic
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">JLG Master Data Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`w-100 ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row className="w-100" style={{ fontWeight: "500" }}>
                <Col lg={4} md={6} xs={12} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col lg={3} md={6} xs={12} className="mb-2">
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col lg={3} md={6} xs={12} className="mb-2">
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="MasterDataReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default JlgMasterDataReport;
